'use client'

import React, { useState, type RefObject } from 'react'

// Dev-only browser trace. The server-side leveled logger imports node:async_hooks
// so it can't be bundled into client components — keep this local.
const traceDev = (msg: string, ...args: unknown[]): void => {
  if (process.env.NODE_ENV === 'development') console.log(`[trace][image-loader] ${msg}`, ...args)
}

export interface UseImageLoaderProps {
  ref: RefObject<HTMLImageElement | null>
  onLoaded?: (debug: string) => void
  origin: string
}

const useImageLoader = (props: UseImageLoaderProps): (() => void) | undefined => {
  const { ref, onLoaded, origin } = props
  const [onLoadCalled, setOnLoadCalled] = useState(false)
  const [_timedOut, setTimedOut] = useState(false)

  const localOnLoad = React.useCallback(() => {
    if (onLoaded) {
      traceDev('onload (%s): local onload being called', origin)
      if (!onLoadCalled) {
        traceDev('onload (%s): calling normal onload due to image loaded', origin)
        onLoaded('normal onload')
        setOnLoadCalled(true)
      }
    }
  }, [onLoaded, onLoadCalled, origin])

  // Sometimes img.onload isn't called, if the image is cached or broken
  // We need to make sure onLoad is always called so set a timer and call
  // it if it wasn't previously called.
  React.useEffect(() => {
    let timer: NodeJS.Timeout
    if (onLoaded && !onLoadCalled) {
      let tries = 0
      const fn = () => {
        if (onLoadCalled) {
          return
        }
        if (ref.current?.complete) {
          onLoaded('image complete onload')
          setOnLoadCalled(true)
        } else {
          tries += 1
          if (tries > 10) {
            onLoaded('timed out onload')
            setOnLoadCalled(true)
            setTimedOut(true)
          } else {
            timer = setTimeout(fn, 500)
          }
        }
      }
      timer = setTimeout(fn, 500)
    }
    return () => {
      if (timer) {
        clearTimeout(timer)
      }
    }
  }, [onLoaded, onLoadCalled, ref, origin])

  return onLoaded ? localOnLoad : undefined
}

export default useImageLoader
